/**
 * @puffin-core/auth-client — MFA verify + OTP operations.
 *
 * ONE internal core (`_verifyMfaCore`), two public projections:
 *   - `verifyMfa({code,method,mfaToken})` — NEVER-throws result-object with the
 *     curated MFA_VERIFY_ERROR_CODES + terminal flag (MFA-LOGIN-CONSOLIDATION-001;
 *     consumed by the no-AuthProvider marketing hook + device flows).
 *   - `verifyMfaForContext(mfaToken, code, method)` — positional, THROWS the
 *     translated AuthError, stores tokens, returns `{user, locations}` (consumed
 *     by `@puffin-core/auth-context` `createAuthContext`, which is UNCHANGED).
 *
 * @module @puffin-core/auth-client/operations/mfa
 */

import { extractData, errorMessage, authError, networkError } from '../internal.js';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';

/** The method whose proof is a passkey assertion (the server's `PASSKEY_METHOD`, Q-EE-34 P5). */
const PASSKEY_METHOD = 'passkey';

/**
 * The server's refusal code, wherever the envelope carries it.
 * @param {Object} [body]
 * @returns {string|null}
 */
function readErrorCode(body) {
  return body?.error?.code ?? body?.code ?? null;
}

/**
 * A code answer's body, or why it cannot be sent.
 * @param {{code: string, method: string}} answer
 * @returns {{body: Object}|{validationError: string}}
 */
function readCodeAnswer({ code, method }) {
  if (!code) return { validationError: 'Verification code is required' };
  if (!method) return { validationError: 'MFA method is required' };
  return { body: { code, method } };
}

/**
 * A passkey answer's body, or why it cannot be sent: the issued challenge's id and the assertion response.
 * @param {{challengeSessionId: string, credential: Object}} assertion
 * @returns {{body: Object}|{validationError: string}}
 */
function readPasskeyAnswer({ challengeSessionId, credential }) {
  if (typeof challengeSessionId !== 'string' || !challengeSessionId) {
    return { validationError: 'Passkey challenge is required' };
  }
  if (credential === null || typeof credential !== 'object' || Array.isArray(credential)) {
    return { validationError: 'Passkey response is required' };
  }
  return { body: { method: PASSKEY_METHOD, challengeSessionId, credential } };
}

/**
 * @param {Object} ctx
 * @returns {Object} MFA methods
 */
export function buildMfaOperations(ctx) {
  const { request, endpoints, tokenStore, errorTranslator } = ctx;

  /**
   * Single HTTP + parse. NEVER throws; returns a neutral discriminated result.
   * @param {{code: string, method: string, mfaToken: string}} params
   * @returns {Promise<Object>}
   */
  async function verifyMfaCore({ code, method, mfaToken, passkeyAssertion }) {
    if (!mfaToken) return { validationError: 'MFA token is required' };
    // A passkey answers with its assertion (ECOSYSTEM-EMAIL-001 Q-EE-34 P5); every other method with a code.
    const answer = passkeyAssertion
      ? readPasskeyAnswer(passkeyAssertion)
      : readCodeAnswer({ code, method });
    if (answer.validationError) return answer;

    const res = await request({
      method: 'POST',
      path: endpoints.verifyMfa,
      body: { ...answer.body, mfaToken },
      auth: 'none',
    });

    if (res.networkError) return { network: true };
    if (!res.ok) {
      // Q-CGI-K11-1 — the failure shape is identified by the ABSENCE of the
      // positive `ok` discriminant the consumers now gate on; the previous
      // `failed: true` marker and the raw `body` passthrough were write-only
      // (nothing ever read either) and are pruned.
      return {
        status: res.status,
        code: readErrorCode(res.body),
        message: errorMessage(res.body, 'MFA verification failed'),
      };
    }
    const rd = extractData(res.body);
    return {
      ok: true,
      user: rd?.user,
      accessToken: rd?.accessToken,
      csrfToken: rd?.csrfToken,
      locations: rd?.locations,
    };
  }

  /**
   * Never-throws result-object projection (MFA-LOGIN-CONSOLIDATION-001).
   * @param {{code: string, method: string, mfaToken: string}} [params]
   * @returns {Promise<{success: boolean, user?: Object, token?: string, csrfToken?: string, status?: number, error?: string, code?: string, terminal?: boolean}>}
   */
  async function verifyMfa({ code, method, mfaToken } = {}) {
    let r;
    try {
      r = await verifyMfaCore({ code, method, mfaToken });
    } catch (error) {
      return { success: false, error: error.message || 'MFA verification failed' };
    }
    if (r.validationError) return { success: false, error: r.validationError };
    if (r.network) return { success: false, error: NETWORK_ERROR };
    // Q-CGI-K11-1 — success is claimed ONLY by the positive discriminant;
    // everything else (the `failed` shape today, and any future core failure
    // kind this projection was not updated for) falls to the failure arm.
    // Failure-as-default is what makes the projection fail CLOSED.
    if (r.ok === true) {
      return { success: true, user: r.user, token: r.accessToken, csrfToken: r.csrfToken };
    }
    // LOGIN-CONVERGENCE W7: the server code travels as `errorCode`, the ONE spelling the translator reads
    // (AUTH-ERROR-VOCABULARY-001). `data: { code }` was retired there, so a curated refusal read as generic.
    const { terminal } = errorTranslator({ status: r.status, errorCode: r.code }, 'mfa');
    return { success: false, status: r.status, error: r.message, code: r.code, terminal };
  }

  /**
   * Positional, throwing projection for `createAuthContext` — stores tokens.
   * @param {string} mfaToken
   * @param {string} code
   * @param {string} method
   * @returns {Promise<{user: Object, locations: Array}>}
   */
  async function verifyMfaForContext(mfaToken, code, method) {
    return installVerifiedSession(await verifyMfaCore({ code, method, mfaToken }));
  }

  /**
   * Positional, throwing PASSKEY verify for `createAuthContext` — stores tokens (ECOSYSTEM-EMAIL-001 Q-EE-34 P5). The
   * assertion answers the challenge {@link getMfaPasskeyOptionsForContext} issued for the same sign-in challenge.
   *
   * @param {string} mfaToken
   * @param {{challengeSessionId: string, credential: Object}} passkeyAssertion - the issued challenge's id and the
   *   browser's formatted assertion response
   * @returns {Promise<{user: Object, locations: Array}>}
   */
  async function verifyMfaPasskeyForContext(mfaToken, passkeyAssertion) {
    return installVerifiedSession(await verifyMfaCore({ mfaToken, passkeyAssertion }));
  }

  /**
   * The passkey ceremony's challenge for the person a LIVE sign-in challenge names (`POST /mfa/verify/passkey/options`,
   * Q-EE-34 P5). Positional, THROWS the translated AuthError — a refusal (the method is not enrolled, the challenge
   * expired) is the same failure a code would meet.
   *
   * @param {string} mfaToken
   * @returns {Promise<Object>} the WebAuthn request options, with the `challengeSessionId` the verify answers with
   */
  async function getMfaPasskeyOptionsForContext(mfaToken) {
    if (!mfaToken) throw new Error('MFA token is required');
    const res = await request({
      method: 'POST',
      path: endpoints.verifyMfaPasskeyOptions,
      body: { mfaToken },
      auth: 'none',
    });
    if (res.networkError) throw errorTranslator({ status: 0 }, 'mfa');
    if (!res.ok) {
      throw errorTranslator({ status: res.status, errorCode: readErrorCode(res.body) }, 'mfa');
    }
    return extractData(res.body);
  }

  /**
   * The ONE ending of a context verify: refuse what the core refused, else store the session and return its person.
   *
   * @param {Object} r - the core's result
   * @returns {{user: Object, locations: Array}}
   * @throws {Error} the validation Error, or the translated AuthError
   */
  function installVerifiedSession(r) {
    if (r.validationError) throw new Error(r.validationError);
    if (r.network) throw errorTranslator({ status: 0 }, 'mfa');
    // Q-CGI-K11-1 — positive discriminant, failure-as-default (see verifyMfa).
    if (r.ok !== true) {
      throw errorTranslator({ status: r.status, errorCode: r.code }, 'mfa');
    }
    if (r.accessToken) {
      tokenStore.setAuth({
        accessToken: r.accessToken,
        csrfToken: r.csrfToken,
        user: r.user,
        locations: r.locations,
      });
    }
    return { user: r.user, locations: r.locations };
  }

  /**
   * Never-throws OTP send (result-object). Method must be 'email' or 'sms'.
   * @param {{method: string, mfaToken: string}} [params]
   * @returns {Promise<{success: boolean, sent?: boolean, method?: string, error?: string}>}
   */
  async function sendMfaOtp({ method, mfaToken } = {}) {
    if (!mfaToken) return { success: false, error: 'MFA token is required' };
    if (method !== 'email' && method !== 'sms') {
      return { success: false, error: 'Method must be "email" or "sms"' };
    }
    let res;
    try {
      res = await request({
        method: 'POST',
        path: endpoints.sendMfaOtp,
        body: { method, mfaToken },
        auth: 'none',
      });
    } catch (error) {
      return { success: false, error: error.message || 'Failed to send verification code' };
    }
    if (res.networkError) return { success: false, error: NETWORK_ERROR };
    if (!res.ok) {
      return { success: false, error: errorMessage(res.body, 'Failed to send verification code') };
    }
    const rd = extractData(res.body);
    return { success: true, sent: rd?.sent, method: rd?.method };
  }

  /**
   * Positional, throwing OTP send for `createAuthContext` (fire-and-forget).
   * @param {string} mfaToken
   * @param {string} method
   * @returns {Promise<*>}
   */
  async function sendMfaOtpForContext(mfaToken, method) {
    const res = await request({
      method: 'POST',
      path: endpoints.sendMfaOtp,
      body: { mfaToken, method },
      auth: 'none',
    });
    if (res.networkError) throw networkError('Failed to send verification code');
    if (!res.ok) throw authError(res, 'Failed to send verification code');
    return extractData(res.body);
  }

  return {
    verifyMfa,
    verifyMfaForContext,
    verifyMfaPasskeyForContext,
    getMfaPasskeyOptionsForContext,
    sendMfaOtp,
    sendMfaOtpForContext,
  };
}
